// MaturaCircleAxesClosedTask.js
// Zadanie maturalne – zamknięte, P/F
// Okrąg w układzie współrzędnych i jego punkty wspólne z osiami OX oraz OY.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TruthInput from "../../../../components/inputs/TruthInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";
import { CIRCLE_GRAPH_COLORS, getCircleGraphRanges } from "../../../../components/GraphPlotter/circleGraphRanges";

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// ----------------- pomocnicze -----------------

function formatShiftedVar(variable, shift) {
  if (shift === 0) return `${variable}^2`;
  const sign = shift > 0 ? "-" : "+";
  return `(${variable} ${sign} ${Math.abs(shift)})^2`;
}

// relacja z osią: "two" – 2 punkty, "tangent" – styczna, "none" – brak
function pickRelation() {
  const all = ["two", "tangent", "none"];
  return all[randInt(0, all.length - 1)];
}

function relationToSentence(axis, relationCode) {
  const axisLatex = axis === "OX" ? "Ox" : "Oy";

  switch (relationCode) {
    case "two":
      return (
        <>
          Okrąg ma dwa punkty wspólne z osią{" "}
          <InlineMath math={axisLatex} />.
        </>
      );
    case "tangent":
      return (
        <>
          Okrąg jest styczny do osi <InlineMath math={axisLatex} />.
        </>
      );
    case "none":
    default:
      return (
        <>
          Okrąg nie ma punktów wspólnych z osią{" "}
          <InlineMath math={axisLatex} />.
        </>
      );
  }
}

// ----------------- GENERATOR -----------------

function createOriginalTask() {
  return {
    type: "MaturaCircleAxesClosedTask",
    data: {
      center: { a: 1, b: -3 },
      r: 2,
      relX: "none",
      relY: "two",
      askedRelX: "none",
      askedRelY: "two",
      s1True: true,
      s2True: true,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 25,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // promień – 2..4, żeby dla środka w [-5,5] dało się mieć wszystkie trzy relacje
  const r = randInt(2, 4);

  // "prawdziwe" relacje względem OX i OY
  const relX = pickRelation(); // względem OX (|b|)
  const relY = pickRelation(); // względem OY (|a|)

  // dobieramy współrzędne środka (a, b) zgodnie z relacjami
  //  - "two": |coord| < r
  //  - "tangent": |coord| = r
  //  - "none": |coord| > r (ale ≤ 5, bo środek w [-5,5])

  function coordForRelation(relation) {
    if (relation === "two") {
      const maxAbs = r - 1; // 0..r-1
      const abs = maxAbs === 0 ? 0 : randInt(0, maxAbs);
      if (abs === 0) return 0;
      return Math.random() < 0.5 ? abs : -abs;
    }
    if (relation === "tangent") {
      const val = r;
      return Math.random() < 0.5 ? val : -val;
    }
    // "none": |coord| > r, ale ≤ 5
    const abs = randInt(r + 1, 5);
    return Math.random() < 0.5 ? abs : -abs;
  }

  const a = coordForRelation(relY); // odległość od OY
  const b = coordForRelation(relX); // odległość od OX

  // zdania w treści – losowo, mogą być prawdziwe lub fałszywe
  const askedRelX = pickRelation();
  const askedRelY = pickRelation();

  const s1True = askedRelX === relX;
  const s2True = askedRelY === relY;

  return {
    type: "MaturaCircleAxesClosedTask",
    data: {
      center: { a, b },
      r,
      relX,
      relY,
      askedRelX,
      askedRelY,
      s1True,
      s2True,
    },
  };
}

// ----------------- TREŚĆ -----------------

export function renderTask({ data }) {
  const { center, r, askedRelX, askedRelY } = data;
  const { a, b } = center;

  const xExpr = formatShiftedVar("x", a);
  const yExpr = formatShiftedVar("y", b);
  const circleEq = `${xExpr} + ${yExpr} = ${r ** 2}`;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        W kartezjańskim układzie współrzędnych dany jest okrąg{" "}
        <InlineMath math={"k"} /> o równaniu:
      </p>

      <BlockMath math={`k: ${circleEq}`} />

      <p>Oceń prawdziwość poniższych zdań.</p>

      <p><InlineMath math="1." /> {relationToSentence("OX", askedRelX)}</p>
      <p><InlineMath math="2." /> {relationToSentence("OY", askedRelY)}</p>
    </div>
  );
}

// ----------------- INPUT (P/F) -----------------

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer || {};
  const setValue = (key, v) => {
    onChange?.({
      ...value,
      [key]: v,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>1.</span>
        <TruthInput
          value={value.s1 || null}
          disabled={disabled}
          onChange={(v) => setValue("s1", v)}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>2.</span>
        <TruthInput
          value={value.s2 || null}
          disabled={disabled}
          onChange={(v) => setValue("s2", v)}
        />
      </div>
    </div>
  );
}

// ----------------- CHECK -----------------

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;
  const { s1True, s2True } = data;
  const { s1, s2 } = userAnswer;

  const s1Ok = (s1 === "P") === s1True;
  const s2Ok = (s2 === "P") === s2True;

  return s1Ok && s2Ok;
}

// ----------------- POPRAWNA ODPOWIEDŹ -----------------

export function renderAnswer({ data }) {
  const { s1True, s2True } = data;

  const s1Latex = `\\text{${s1True ? "P" : "F"}}`;
  const s2Latex = `\\text{${s2True ? "P" : "F"}}`;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>Poprawna odpowiedź:</p>
      <p>
        <InlineMath math="1." /> <InlineMath math={s1Latex} />
      </p>
      <p>
        <InlineMath math="2." /> <InlineMath math={s2Latex} />
      </p>
    </div>
  );
}

// ----------------- ODPOWIEDŹ UŻYTKOWNIKA -----------------

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }

  const s1 = userAnswer.s1 || "-";
  const s2 = userAnswer.s2 || "-";

  const s1Latex = s1 === "-" ? "-" : `\\text{${s1}}`;
  const s2Latex = s2 === "-" ? "-" : `\\text{${s2}}`;

  return (
    <div style={{ marginTop: 6, textAlign: "left" }}>
      <p>Twoja odpowiedź:</p>
      <p>
        1. <InlineMath math={s1Latex} />
      </p>
      <p>
        2. <InlineMath math={s2Latex} />
      </p>
    </div>
  );
}

// ----------------- WYJAŚNIENIE -----------------

export function renderExplanation({ data }) {
  const { center, r, relX, relY, s1True, s2True } = data;
  const { a, b } = center;
  const graphCenter = { x: a, y: b };
  const { xRange, yRange, graphSize } = getCircleGraphRanges({
    circles: [{ center: graphCenter, radius: r }],
    points: [graphCenter],
  });

  const xExpr = formatShiftedVar("x", a);
  const yExpr = formatShiftedVar("y", b);
  const circleEq = `${xExpr} + ${yExpr} = ${r ** 2}`;

  const distX = Math.abs(b); // odległość od OX
  const distY = Math.abs(a); // odległość od OY

  const describeRel = (rel, axis) => {
    const axisLatex = axis === "OX" ? "Ox" : "Oy";
    switch (rel) {
      case "two":
        return <>okrąg ma dwa punkty wspólne z osią <InlineMath math={axisLatex} /></>;
      case "tangent":
        return <>okrąg jest styczny do osi <InlineMath math={axisLatex} /></>;
      default:
        return <>okrąg nie ma punktów wspólnych z osią <InlineMath math={axisLatex} /></>;
    }
  };

  const relXText = describeRel(relX, "OX");
  const relYText = describeRel(relY, "OY");

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <h3>Krok 1. Odczytanie środka i promienia</h3>
      <p>
        Z równania okręgu{" "}
        <InlineMath math={circleEq} /> możemy zapisać je z oznaczeniem
        okręgu:
      </p>
      <BlockMath math={`k: ${circleEq}`} />
      <p>
        Środek okręgu ma współrzędne{" "}
        <InlineMath math={`S = (${a}, ${b})`} />, a promień{" "}
        <InlineMath math={`r = ${r}`} />.
      </p>

      <div
        style={{
          marginTop: 8,
          marginBottom: 8,
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ width: `min(100%, ${graphSize}px)` }}>
          <GraphViewer
            points={{
              S: { ...graphCenter, label: "S", color: CIRCLE_GRAPH_COLORS.point, textColor: CIRCLE_GRAPH_COLORS.point },
            }}
            circles={[
              {
                center: graphCenter,
                radius: r,
                color: CIRCLE_GRAPH_COLORS.circle,
                label: "k",
              },
            ]}
            width={graphSize}
            height={graphSize}
            showGrid
            xRange={xRange}
            yRange={yRange}
          />
        </div>
      </div>

      <h3>Krok 2. Odległość środka od osi</h3>
      <p>
        Odległość środka od osi{" "}
        <InlineMath math={"Ox"} /> równa się{" "}
        <InlineMath math={`|y_S| = |${b}| = ${distX}`} />.
      </p>
      <p>
        Odległość środka od osi{" "}
        <InlineMath math={"Oy"} /> równa się{" "}
        <InlineMath math={`|x_S| = |${a}| = ${distY}`} />.
      </p>

      <h3>Krok 3. Porównanie z promieniem</h3>
      <p>
        Dla osi <InlineMath math={"Ox"} /> porównujemy{" "}
        <InlineMath math={"|y_S|"} /> z promieniem:
      </p>
      <BlockMath math={`|y_S| = ${distX},\\ r = ${r}`} />
      <p>
        Jeśli <InlineMath math={"|y_S| < r"} />, okrąg ma dwa punkty wspólne z
        osią; jeśli <InlineMath math={"|y_S| = r"} />, jest styczny; jeśli{" "}
        <InlineMath math={"|y_S| > r"} />, nie ma punktów wspólnych.
      </p>

      <p>
        W naszym zadaniu otrzymujemy, że {relXText}.
      </p>

      <p>
        Analogicznie dla osi <InlineMath math={"Oy"} />:
      </p>
      <BlockMath math={`|x_S| = ${distY},\\ r = ${r}`} />
      <p>Wychodzi, że {relYText}.</p>

      <h3>Krok 4. Ocena zdań</h3>
      <p>
        Zdanie <InlineMath math="1" /> opisuje sytuację względem osi{" "}
        <InlineMath math={"Ox"} /> i jest{" "}
        <strong>{s1True ? "prawdziwe" : "fałszywe"}</strong>, ponieważ
        rzeczywiście {relXText}.
      </p>
      <p>
        Zdanie <InlineMath math="2" /> opisuje sytuację względem osi{" "}
        <InlineMath math={"Oy"} /> i jest{" "}
        <strong>{s2True ? "prawdziwe" : "fałszywe"}</strong>, ponieważ
        rzeczywiście {relYText}.
      </p>
    </div>
  );
}
